import { useState } from 'react';
import { ChevronLeft, MapPin, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQualifyWizard } from '@/features/producer-qualify/wizard/useQualifyWizard';
import { StepDots } from '@/components/for-producers/StepDots';
import { QualifyStepPlaceSelect } from '@/components/for-producers/QualifyStepPlaceSelect';
import { QualifyStepProducerName } from '@/components/for-producers/QualifyStepProducerName';
import { QualifyStepOfferings } from '@/components/for-producers/QualifyStepOfferings';
import { QualifyMissingPlaceDialog } from '@/components/for-producers/QualifyMissingPlaceDialog';
import { formatOfferings } from '@/features/producer-qualify/offeringLabels';

export default function ForProducersQualify() {
  const wiz = useQualifyWizard();
  const [showMissingPlace, setShowMissingPlace] = useState(false);

  if (!wiz.hydrated) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Back */}
      <div className="p-4">
        <button
          onClick={wiz.goBack}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Tillbaka
        </button>
      </div>

      {/* Dots */}
      <div className="pt-4 pb-2">
        <StepDots total={wiz.totalSteps} current={wiz.currentStepIndex} />
      </div>

      <main className="flex-1 flex flex-col items-center px-6 pb-20 pt-8 md:pt-12">
        <div className="max-w-md w-full space-y-6">
          {/* Review summaries for completed earlier steps */}
          {wiz.currentStep !== 'place' && wiz.selectedPlace && (
            <ReviewRow
              label="Vald plats"
              value={wiz.selectedPlace.name}
              sublabel={
                [wiz.selectedPlace.municipality, wiz.selectedPlace.landskap]
                  .filter(Boolean)
                  .join(' · ') || undefined
              }
              onEdit={() => wiz.goToStep('place')}
            />
          )}
          {wiz.currentStep === 'offerings' && wiz.producerName.trim() && (
            <ReviewRow
              label="Verksamhet"
              value={wiz.producerName.trim()}
              onEdit={() => wiz.goToStep('name')}
            />
          )}

          {/* Active step */}
          {wiz.currentStep === 'place' && (
            <QualifyStepPlaceSelect
              value={wiz.selectedPlace}
              onChange={wiz.setSelectedPlace}
              onMissingPlace={() => setShowMissingPlace(true)}
            />
          )}
          {wiz.currentStep === 'name' && (
            <QualifyStepProducerName
              value={wiz.producerName}
              onChange={wiz.setProducerName}
            />
          )}
          {wiz.currentStep === 'offerings' && (
            <QualifyStepOfferings
              value={wiz.offerings}
              onChange={wiz.setOfferings}
            />
          )}

          {/* Submission summary on last step */}
          {wiz.isLastStep && (
            <div className="rounded-xl border border-border/60 bg-muted/20 p-5 space-y-4 mt-2">
              <h3 className="text-sm font-semibold text-foreground">Sammanfattning</h3>

              <SummaryRow
                label="Plats"
                value={
                  wiz.selectedPlace
                    ? [wiz.selectedPlace.name, wiz.selectedPlace.landskap].filter(Boolean).join(', ')
                    : '–'
                }
                onEdit={() => wiz.goToStep('place')}
              />
              <SummaryRow
                label="Verksamhet"
                value={wiz.producerName.trim() || '–'}
                onEdit={() => wiz.goToStep('name')}
              />
              <SummaryRow
                label="Erbjuder"
                value={formatOfferings(wiz.offerings)}
                onEdit={() => wiz.goToStep('offerings')}
              />

              {/* Contact section */}
              <div className="border-t border-border/40 pt-4 mt-4">
                <p className="text-xs text-muted-foreground mb-1">Kontakt</p>
                {wiz.isLoggedIn ? (
                  <>
                    <p className="text-sm text-foreground">{wiz.contactEmail}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Vi hör av oss hit.</p>
                  </>
                ) : (
                  <div className="space-y-1.5">
                    <label htmlFor="qualify-email" className="text-sm text-foreground font-medium">
                      E-postadress
                    </label>
                    <Input
                      id="qualify-email"
                      type="email"
                      placeholder="namn@exempel.se"
                      value={wiz.contactEmail}
                      onChange={(e) => wiz.setContactEmail(e.target.value)}
                      autoComplete="email"
                    />
                    <p className="text-xs text-muted-foreground">Så att vi kan höra av oss till er.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="max-w-md w-full mt-10">
          {wiz.submitError && (
            <p className="text-sm text-destructive mb-3 text-center">{wiz.submitError}</p>
          )}
          <Button
            onClick={wiz.goNext}
            disabled={!wiz.canGoNext}
            className="w-full"
          >
            {wiz.ctaLabel}
          </Button>
          {wiz.validationHint && (
            <p className="text-xs text-muted-foreground mt-3 text-center">
              {wiz.validationHint}
            </p>
          )}
          {wiz.isLastStep && (
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Vi hör av oss inom några dagar.
            </p>
          )}
        </div>
      </main>

      <QualifyMissingPlaceDialog
        open={showMissingPlace}
        onOpenChange={setShowMissingPlace}
      />
    </div>
  );
}

/* ── Compact review row ─────────────────────────────── */

function ReviewRow({
  label,
  value,
  sublabel,
  onEdit,
}: {
  label: string;
  value: string;
  sublabel?: string;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 px-4 py-3">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground truncate">{value}</p>
        {sublabel && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3 shrink-0" />
            {sublabel}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="text-xs text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1 shrink-0"
      >
        <Pencil className="h-3 w-3" />
        Ändra
      </button>
    </div>
  );
}

/* ── Summary row for final step ─────────────────────── */

function SummaryRow({
  label,
  value,
  onEdit,
}: {
  label: string;
  value: string;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground truncate">{value}</p>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="text-xs text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1 shrink-0"
      >
        <Pencil className="h-3 w-3" />
        Ändra
      </button>
    </div>
  );
}
